import { useCallback, useEffect, useRef } from 'react';

const MESSAGE_TYPE = 'event:update';

function channelName(eventId) {
  return `communityshare:event:${eventId}`;
}

/**
 * Cross-tab updates for an event over the BroadcastChannel API. The share
 * dialog broadcasts field changes; the public share tab listens and reacts.
 *
 * Message shape: { type: 'event:update', eventId, fields?: { title, imageUrl } }
 *
 * `fields` is optional: a message with specific fields lets a listener apply
 * them optimistically, while a fieldless message is a generic "this event
 * changed, re-pull it" signal (e.g. broadcast by the manage dialog, which knows
 * the record changed but not which public fields it maps to).
 *
 * @param {number|string} eventId
 * @param {(message: { type: string, eventId: number|string, fields?: object }) => void} [onMessage]
 *   Called for each incoming message. Omit on send-only senders.
 * @returns {(fields?: { title?: string, imageUrl?: string }) => void} broadcast
 */
export default function useEventChannel(eventId, onMessage) {
  const channelRef = useRef(null);

  // Keep the latest callback without re-subscribing the channel each render.
  const onMessageRef = useRef(onMessage);
  onMessageRef.current = onMessage;

  useEffect(() => {
    if (!eventId || typeof BroadcastChannel === 'undefined') {
      return undefined;
    }

    const channel = new BroadcastChannel(channelName(eventId));
    channelRef.current = channel;
    channel.onmessage = (event) => {
      if (onMessageRef.current) {
        onMessageRef.current(event.data);
      }
    };

    return () => {
      channel.close();
      channelRef.current = null;
    };
  }, [eventId]);

  return useCallback((fields) => {
    const channel = channelRef.current;
    if (channel) {
      channel.postMessage({ type: MESSAGE_TYPE, eventId, fields });
    }
  }, [eventId]);
}
